import React, { useCallback, useMemo, useRef, useState, useEffect, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { LoginScreen } from "./features/auth/LoginScreen";
import { PublicVerify } from "./features/auth/PublicVerify";
import { ResetPassword } from "./features/auth/ResetPassword";
// Neue Shell + Sidebar Navigation
import AppShell from "./app/AppShell";
import { QmExportWatcher } from "./components/common/QmExportWatcher";
import NavSidebar from "./components/nav/AppSidebar";
import { menuItems, type TabKey } from "./config/menu";

// Security: session timeout
import { useSessionTimeout } from "./hooks/useSessionTimeout";

/* =========================================================
   Typen & Utils
   ========================================================= */
import type { Role } from "./types/app";
import { SystemAdminPanel } from "./modules/SystemAdminPanel";
// Lazy load feature modules for better bundle splitting
const EmployeesTabComponent = React.lazy(() => import("./features/va"));
const Pflegevisite = React.lazy(() => import("./features/md-checklist"));
const Complaints = React.lazy(() => import("./features/complaints"));
// Import QmHandbuch separately to get both default and named export
const QmHandbuchModule = React.lazy(() => import("./features/handbook"));
const WikiModule = React.lazy(() => import("./features/wiki"));
const QmhImportModule = React.lazy(() => import("./features/qmh-import"));
const QmVorlagenModule = React.lazy(() => import("./features/qm-vorlagen"));
const Verfahrensanweisungen = React.lazy(() => import("./features/procedures"));
const FortbildungenModule = React.lazy(() => import("./features/training"));
const MDChecklistModule = React.lazy(() => import("./features/md-checklist/MDChecklist"));
const DashboardModule = React.lazy(() => import("./features/dashboard"));
const FortbildungsbibliothekModule = React.lazy(() => import("./features/admin"));
const TemplateLibraryModule = React.lazy(() => import("./features/template-library"));
const WissenswertesModule = React.lazy(() => import("./features/wissenswertes"));
const OrganisationModule = React.lazy(() => import("./features/organisation"));
const DokumenteModule = React.lazy(() => import("./features/dokumente"));
const ProzesseModule = React.lazy(() => import("./features/prozesse"));
const ProfileTabModule = React.lazy(() => import("./features/profile"));
const Iso9001Module = React.lazy(() => import("./features/iso9001"));
const Iso15224Module = React.lazy(() => import("./features/iso15224"));
const HelpModule = React.lazy(() => import("./features/hilfe"));
import { ROLE_KEY, BIZ_SELECT_KEY } from "./utils/constants";
import { getActiveBusinessId } from "./utils/business";
import { apiUrl, authFetch, currentAuthUser, clearAuth, type AuthUser } from "./utils/api";
import { useLogout } from "./hooks/useLogout";
import { HelpCircle } from "./components/icons";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { GlobalNotifications } from "./components/common/GlobalNotifications";
import { TrialBanner } from "./components/common/TrialBanner";
import { GlobalSearch } from "./components/common/GlobalSearch";
import { GuidedTour, type TourStep } from "./components/common/GuidedTour";
import { TourWelcomePrompt } from "./components/common/TourWelcomePrompt";
import { ORIENTIERUNG, TUTORIALS } from "./features/tutorials/tutorials";
import { WillkommenDialog } from "./features/tutorials/WillkommenDialog";
import { START_TUTORIAL, markTutorialDone } from "./features/tutorials/state";
import { showGlobalToast } from "./utils/globalNotify";
import { PageSkeleton } from "./components/common/Skeleton";
import { Compass } from "lucide-react";

const TOUR_PREF_PREFIX = "sonne_tour_pref_v1_";

const AREA_TOUR_PREFIX = "sonne_tour_area_v1_";

// Pro-Bereich-Touren: erscheinen beim ersten Öffnen eines Bereichs und erklären
// die sichtbaren Punkte/Buttons. Ziele: [data-tour="area-header"] (gemeinsame
// PageHeader-Komponente), spezielle data-tour-Marker, oder Button-Text (findText,
// im Inhaltsbereich gesucht). Schritte ohne Ziel = zentrierter Hinweis.
function areaToursFor(role: Role): Partial<Record<string, TourStep[]>> {
  const header = (title: string, body: string): TourStep => ({ selector: '[data-tour="area-header"]', title, body });
  const btn = (findText: string, title: string, body: string): TourStep => ({ findText, title, body });
  const note = (title: string, body: string): TourStep => ({ title, body });

  if (role === "mitarbeitende") {
    return {
      dashboard: [
        header("Dein Dashboard", "Dein Startpunkt: offene Kenntnisnahmen und Fortbildungen auf einen Blick."),
        { selector: '[data-tour="global-search"]', title: "Globale Suche", body: "Mit Strg+K oder hier findest du sofort das richtige Dokument, eine Verfahrensanweisung oder Fortbildung." },
        btn("Wonach suchst du", "Schnell finden", "Öffnet die Suche – tippe einen Begriff und komm direkt zum passenden Dokument."),
        btn("Alle Fortbildungen öffnen", "Fortbildungen", "Zeigt alle deine Fortbildungen. „Zur Kenntnis genommen“ bestätigt die Teilnahme."),
        btn("Alle Verfahrensanweisungen öffnen", "Verfahrensanweisungen", "Öffnet die dir zugewiesenen Verfahrensanweisungen zur Kenntnisnahme."),
      ],
      qmhandbuch: [
        note("QM-Handbuch", "Hier liest du das QM-Handbuch deines Pflegedienstes. Dir zugewiesene Abschnitte bestätigst du zur Kenntnisnahme."),
        btn("PDF Export", "PDF-Export", "Exportiert das QM-Handbuch als PDF zum Ansehen oder Ausdrucken."),
      ],
      va: [
        header("Verfahrensanweisungen", "Die für dich freigegebenen Verfahrensanweisungen. Zugewiesene bestätigst du zur Kenntnisnahme."),
        note("Suchen & filtern", "Oben kannst du nach Titel suchen und nach Status oder Tags filtern."),
        btn("PDF Export", "PDF-Export", "Exportiert die Liste als PDF."),
      ],
      fortbildungen: [
        header("Fortbildungen", "Deine Fortbildungen und Unterlagen. Durchgeführte Fortbildungen bestätigst du zur Kenntnisnahme."),
        btn("PDF Export", "PDF-Export", "Exportiert die Fortbildungsliste als PDF."),
      ],
      prozesse: [
        header("Prozesse", "Hier siehst du die Abläufe deines Pflegedienstes und wie sie mit dem QM zusammenhängen."),
      ],
    };
  }

  // Admin / PDL: ausführliche Touren mit allen sichtbaren Buttons je Bereich.
  return {
    dashboard: [
      header("Dashboard", "Dein Startbildschirm mit Überblick, offenen Themen und Schnellzugriffen."),
      { selector: '[data-tour="global-search"]', title: "Globale Suche", body: "Mit Strg+K oder hier findest du sofort Dokumente, Verfahrensanweisungen, Mitarbeitende und mehr." },
      btn("Zur MD", "MD-Qualität", "Die Kachel zeigt deinen Erfüllungsgrad. „Zur MD-Checkliste“ öffnet die Prüf-Checkliste."),
      note("Meine Aufgaben", "Bündelt dir zugewiesene Aufgaben. Über „Neu“ weist du Mitarbeitenden Aufgaben zu."),
      note("Kacheln", "Pflegevisiten, Verfahrensanweisungen und Mitarbeitende zeigen aktuelle Zahlen – „Öffnen“ bringt dich direkt in den Bereich."),
      btn("Zertifikate erstellen", "Zertifikate erstellen", "Schnellzugriff zum Erstellen von Fortbildungs-Zertifikaten."),
      btn("Druck", "Druck-Hintergrund", "Hinterlege ein Hintergrundbild/Wasserzeichen für PDF-Ausdrucke."),
    ],
    qmhandbuch: [
      btn("Inhalte", "Inhalte", "Zeigt die Handbuch-Abschnitte mit ihren Inhalten (Standardansicht)."),
      { selector: '[data-tour="qmh-vorlagen"]', title: "Vorlagen", body: "Fertige Muster ansehen und mit einem Klick übernehmen – auch das ganze Handbuch auf einmal." },
      note("Nur meine Aufgaben", "Diese Checkbox blendet nur die dir zugewiesenen Abschnitte ein."),
      btn("Struktur bearbeiten", "Struktur bearbeiten", "Öffnet den Inline-Editor: Abschnitte direkt in der Ansicht hinzufügen, umbenennen und per Drag & Drop sortieren."),
      btn("PDF Export", "PDF-Export", "Exportiert das komplette QM-Handbuch als PDF."),
    ],
    va: [
      header("Verfahrensanweisungen", "Erstelle, verwalte und teile Verfahrensanweisungen zur Kenntnisnahme zu."),
      note("Suchen & filtern", "Oben suchst du und filterst nach Status, Tags oder nur deinen Aufgaben."),
      { selector: '[data-tour="va-vorlagen"]', title: "Vorlagen", body: "Wechselt zur Vorlagen-Ansicht: Muster ansehen und übernehmen." },
      btn("Neu", "Neu", "Neue Verfahrensanweisung erstellen – leer oder aus einer Vorlage."),
      btn("Mehrfach löschen", "Mehrfach löschen", "Mehrere Verfahrensanweisungen auswählen und gemeinsam löschen."),
      btn("PDF Export", "PDF-Export", "Exportiert die Liste der Verfahrensanweisungen als PDF."),
    ],
    fortbildungen: [
      header("Fortbildungen", "Plane Fortbildungen, lade Unterlagen hoch und lass Teilnahmen bestätigen."),
      { selector: '[data-tour="fortbildung-neu"]', title: "Neu", body: "Neue Fortbildung anlegen oder eine aus der Bibliothek übernehmen." },
      btn("PDF Export", "PDF-Export", "Exportiert die Fortbildungsliste als PDF."),
      note("Hintergrund (Zertifikat)", "Mit dieser Checkbox nutzt du den hinterlegten Zertifikat-Hintergrund."),
    ],
    mitarbeitende: [
      header("Mitarbeitende", "Lege Mitarbeitende an und pflege Qualifikationen. Qualifikationen wie PDL oder QMB füllen automatisch Platzhalter in Vorlagen."),
      btn("Mitarbeiter importieren", "Importieren", "Mehrere Mitarbeitende auf einmal per Datei importieren."),
      btn("Dokumente hochladen", "Dokumente hochladen", "Dokumente sammeln und Mitarbeitenden zuordnen."),
      btn("PDF Export", "PDF-Export", "Exportiert die Mitarbeiterliste als PDF."),
      btn("anlegen", "Neu anlegen", "Öffnet das Formular, um einen neuen Mitarbeiter oder Admin anzulegen."),
    ],
    vorlagenbibliothek: [
      header("Vorlagenbibliothek", "Alle Muster-Vorlagen an einem Ort – ansehen und für deinen Pflegedienst übernehmen."),
      btn("Alle", "Typ-Filter", "Filtert nach Vorlagentyp: Alle, Verfahrensanweisungen oder QM-Handbuch."),
      note("Suche", "Über das Suchfeld findest du eine Vorlage nach Titel."),
      btn("Ansehen & übernehmen", "Ansehen & übernehmen", "Öffnet die Vorschau; dort übernimmst du die Vorlage – automatisch mit deinen Daten befüllt."),
    ],
    organisation: [
      header("Organisation", "Stammdaten deines Pflegedienstes – füllen die Platzhalter in übernommenen Vorlagen."),
      note("Uploads", "Über die Felder hinterlegst du Logo, Versorgungsvertrag, Inbetriebnahme und Druck-Hintergrund."),
      btn("Speichern", "Speichern", "Sichert deine Stammdaten."),
    ],
    dokumente: [
      header("Dokumente", "Vertrags- und Dokumentvorlagen für Mitarbeitende und Klienten verwalten."),
      btn("Verträge", "Verträge", "Vertragsvorlagen (z. B. Arbeitsverträge)."),
      btn("Allgemeine Dokumente", "Allgemeine Dokumente", "Sonstige Dokumentvorlagen."),
      btn("Unterlagen", "Unterlagen (Zuteilung)", "Onboarding-Pakete je Qualifikation zusammenstellen."),
      btn("Hochladen", "Hochladen", "Neue Vorlage als Word-Datei hochladen."),
    ],
    prozesse: [
      header("Prozesse", "Bilde Abläufe als Prozesse ab und verknüpfe sie mit QM-Handbuch und Verfahrensanweisungen."),
      btn("Prozess anlegen", "Prozess anlegen", "Legt einen neuen Prozess an und öffnet den Editor."),
    ],
    beschwerdemanagement: [
      header("Verbesserungsliste", "Erfasse Beschwerden und Verbesserungen und verfolge die Maßnahmen."),
      note("Filter", "Oben filterst du nach Status, Melder und Verantwortlichem."),
      btn("Neue Beschwerde", "Neue Beschwerde", "Öffnet das Formular zum Erfassen einer neuen Beschwerde/Verbesserung."),
    ],
    md: [
      header("MD-Checkliste", "Bereite dich auf die MD-Qualitätsprüfung vor und behalte deinen Erfüllungsgrad im Blick."),
      note("Version & Filter", "Oben wählst du die Checklisten-Version, suchst und filterst nach Antworten."),
      btn("Neuer Punkt", "Neuer Punkt", "Fügt der Checkliste einen neuen Prüfpunkt hinzu."),
      btn("Audit starten", "Audit starten", "Startet ein neues MD-Audit."),
      btn("Änderungen speichern", "Änderungen speichern", "Veröffentlicht Änderungen an der Checklisten-Vorlage."),
      btn("Zurücksetzen", "Zurücksetzen", "Setzt die Checkliste zurück."),
    ],
    pflegevisite: [
      header("Pflegevisite", "Dokumentiere Pflegevisiten strukturiert."),
      btn("Neue Pflegevisite", "Neue Pflegevisite", "Erfasst eine neue Pflegevisite."),
      btn("Vorhandene", "Vorhandene", "Zeigt bereits erfasste Pflegevisiten."),
    ],
    wissenswertes: [
      header("Wissenswertes QM", "Hintergrundwissen und Erklärungen rund ums Qualitätsmanagement."),
      btn("Standard-Artikel aktualisieren", "Standard-Artikel", "Lädt bzw. aktualisiert die vorgefertigten Wissensartikel."),
    ],
  };
}
type TabId =
  | "dashboard"
  | "adminpanel"
  | "fortbildungsbibliothek"
  | "vorlagenbibliothek"
  | "qmvorlagen"
  | "qmhimport"
  | "organisation"
  | "dokumente"
  | "prozesse"
  | "md"
  | "iso9001"
  | "iso15224"
  | "va"
  | "fortbildungen"
  | "pflegevisite"
  | "pflegeberatung"
  | "mitarbeitende"
  | "qmhandbuch"
  | "wiki"
  | "beschwerdemanagement"
  | "wissenswertes"
  | "profil"
  | "hilfe";


function resolveRoleFromUser(user: AuthUser | null): Role | null {
  if (!user) return null;
  const storedBizId = localStorage.getItem(BIZ_SELECT_KEY);
  if (user.role === "system_admin") {
    // System-Admin mit ausgewaehltem Mandanten arbeitet als Admin IM Mandanten,
    // behaelt aber zusaetzlich die System-Admin-Bereiche (siehe isSysAdmin).
    return storedBizId ? "admin" : "system_admin";
  }
  const activeBizId = storedBizId || user.business_id;
  if (activeBizId && Array.isArray(user.businesses)) {
    const match = user.businesses.find((b) => String(b.business_id) === String(activeBizId));
    if (match?.role === "business_admin") return "admin";
    if (match?.role === "worker") return "mitarbeitende";
  }
  if (user.role === "business_admin") return "admin";
  if (user.role === "worker") return "mitarbeitende";
  return "admin";
}

function useRole(): [Role, (r: Role) => void] {
  const [role, setRole] = useState<Role>(() => {
    const saved = localStorage.getItem(ROLE_KEY);
    const authUser = currentAuthUser();
    const resolved = resolveRoleFromUser(authUser);
    if (resolved) return resolved;
    return (saved === "mitarbeitende" || saved === "admin" || saved === "system_admin") ? (saved as Role) : "admin";
  });
  useEffect(() => { localStorage.setItem(ROLE_KEY, role); }, [role]);
  return [role, setRole];
}


function App() {
  // Prüflink-Route: #/pruefung/:token -> Public Verification View
  const hash = typeof window !== 'undefined' ? window.location.hash || '' : '';
  const verifyToken = hash.startsWith('#/pruefung/') ? hash.replace('#/pruefung/','') : null;
  const resetToken = hash.startsWith('#/reset-password/') ? hash.replace('#/reset-password/','') : null;
  const [role, setRole] = useRole();
  const [authedUser, setAuthedUser] = useState<AuthUser | null>(() => currentAuthUser());
  const authed = !!authedUser;
  // Echter System-Admin (unabhaengig davon, ob er gerade in einem Mandanten arbeitet).
  const isSysAdmin = authedUser?.role === "system_admin";
  // System-Admin, der einen Mandanten "betreten" hat (Mandant ausgewaehlt): sieht
  // die Mandanten-Ansicht UND behaelt die System-Admin-Bereiche.
  const sysAdminInBusiness = isSysAdmin && !!getActiveBusinessId();
  const [tab, setTab] = useState<TabId>("dashboard");
  const [searchOpen, setSearchOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [activeSteps, setActiveSteps] = useState<TourStep[]>([]);
  const [activeTourTab, setActiveTourTab] = useState<TabId | null>(null);
  // Laufendes Mitmach-Tutorial (Erste Schritte); null = Bereichs-Tour oder keine.
  const [activeTutorialId, setActiveTutorialId] = useState<string | null>(null);
  const tourActiveRef = useRef(false);
  tourActiveRef.current = tourActive;
  const [tourPref, setTourPref] = useState<"yes" | "no" | null>(() => {
    const scope = getActiveBusinessId() ?? currentAuthUser()?.id;
    if (scope == null) return null;
    try {
      const value = localStorage.getItem(`${TOUR_PREF_PREFIX}${scope}`);
      return value === "yes" || value === "no" ? value : null;
    } catch { return null; }
  });
  const [helpReturnTab, setHelpReturnTab] = useState<TabId>("dashboard");
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
  const [orgLogoAlt, setOrgLogoAlt] = useState<string | null>(null);
  const [orgTrainingBgUrl, setOrgTrainingBgUrl] = useState<string | null>(null);
  const [orgTrainingBgName, setOrgTrainingBgName] = useState<string | null>(null);
  const [iso9001Enabled, setIso9001Enabled] = useState(false);
  const [iso15224Enabled, setIso15224Enabled] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState<string | null>(null);

  // Silent session timeout monitoring (auto-logout on inactivity, no UI)
  useSessionTimeout(authed);

  // Reset all auth-derived UI state back to the logged-out baseline.
  const resetAuthUiState = useCallback(() => {
    setAuthedUser(null);
    setRole("admin");
    setTab("dashboard");
    setOrgLogoUrl(null);
    setOrgLogoAlt(null);
    setOrgTrainingBgUrl(null);
    setOrgTrainingBgName(null);
  }, [setRole]);

  const logout = useLogout({ onAfter: resetAuthUiState });

  // Auto-logout on session end: either a 401 (auth:expired — api.ts has already
  // cleared storage) or inactivity/absolute timeout (session:expired — clear here).
  useEffect(() => {
    const SESSION_ENDED_MSG = "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.";
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    const showExpiredMessage = () => {
      setSessionExpiredMsg(SESSION_ENDED_MSG);
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setSessionExpiredMsg(null), 8000);
    };
    const onAuthExpired = () => {
      resetAuthUiState();
      showExpiredMessage();
    };
    const onSessionExpired = () => {
      clearAuth();
      resetAuthUiState();
      showExpiredMessage();
    };
    window.addEventListener("auth:expired", onAuthExpired);
    window.addEventListener("session:expired", onSessionExpired);
    return () => {
      window.removeEventListener("auth:expired", onAuthExpired);
      window.removeEventListener("session:expired", onSessionExpired);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [resetAuthUiState]);
  useEffect(() => {
    if (!authed) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === "k" || event.key === "K")) {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [authed]);
  const areaTours = useMemo(() => areaToursFor(role), [role]);
  // Tour-Zustand pro Pflegedienst (Business) merken – bleibt über Logins hinweg
  // erhalten (User-ID ist optional und wäre unzuverlässig).
  const tourScopeId = authedUser ? (getActiveBusinessId() ?? authedUser.id ?? null) : null;
  const areaSeenKey = useCallback(
    (t: string) => (tourScopeId != null ? `${AREA_TOUR_PREFIX}${tourScopeId}_${t}` : null),
    [tourScopeId]
  );

  const startAreaTour = useCallback((t: string, force = false) => {
    if (role === "system_admin") return;
    if (typeof window === "undefined" || !window.matchMedia("(min-width: 768px)").matches) return;
    const steps = areaTours[t];
    if (!steps || steps.length === 0) return;
    if (!force) {
      const key = areaSeenKey(t);
      let seen = false;
      try { seen = key ? localStorage.getItem(key) === "1" : false; } catch { /* ignore */ }
      if (seen) return;
    }
    setActiveSteps(steps);
    setActiveTourTab(t as TabId);
    setTourActive(true);
  }, [areaTours, role, areaSeenKey]);

  const finishTour = useCallback((completed?: boolean) => {
    setTourActive(false);
    if (activeTutorialId) {
      if (completed) {
        markTutorialDone(activeTutorialId);
        showGlobalToast("Geschafft! Tutorial abgeschlossen.", "success");
      }
      setActiveTutorialId(null);
      return;
    }
    const key = activeTourTab ? areaSeenKey(activeTourTab) : null;
    if (key) { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } }
    setActiveTourTab(null);
  }, [activeTourTab, activeTutorialId, areaSeenKey]);

  // Mitmach-Tutorials (Erste Schritte) – gestartet über Dashboard-Karte oder Hilfe.
  const startTutorial = useCallback((id: string) => {
    const tutorial = TUTORIALS.find((t) => t.id === id);
    if (!tutorial || role === "system_admin" || role === "mitarbeitende") return;
    if (typeof window !== "undefined" && !window.matchMedia("(min-width: 768px)").matches) {
      showGlobalToast("Die Tutorials sind auf Computer und Tablet verfügbar.", "info");
      return;
    }
    setActiveTourTab(null);
    setActiveTutorialId(id);
    setActiveSteps(tutorial.steps);
    setTourActive(true);
  }, [role]);

  useEffect(() => {
    const handler = (event: Event) => {
      const id = (event as CustomEvent).detail;
      if (typeof id === "string") startTutorial(id);
    };
    window.addEventListener(START_TUTORIAL, handler);
    return () => window.removeEventListener(START_TUTORIAL, handler);
  }, [startTutorial]);

  // Tour-Präferenz nachladen, wenn sich der eingeloggte Nutzer ändert.
  useEffect(() => {
    if (tourScopeId == null) { setTourPref(null); return; }
    try {
      const value = localStorage.getItem(`${TOUR_PREF_PREFIX}${tourScopeId}`);
      setTourPref(value === "yes" || value === "no" ? value : null);
    } catch { setTourPref(null); }
  }, [tourScopeId]);

  const setTourPreference = useCallback((pref: "yes" | "no") => {
    setTourPref(pref);
    if (tourScopeId != null) { try { localStorage.setItem(`${TOUR_PREF_PREFIX}${tourScopeId}`, pref); } catch { /* ignore */ } }
  }, [tourScopeId]);

  // Willkommen beim ersten Login (Admins): zeigt, wo die Anleitungen sind, und erklärt
  // Platzhalter. Erscheint einmal je Pflegedienst – bei neuen Admins (Tour-Frage noch
  // unbeantwortet) und während der Testphase.
  const welcomeKey = tourScopeId != null ? `sonne_welcome_v1_${tourScopeId}` : null;
  const [welcomeSeen, setWelcomeSeen] = useState(true);
  useEffect(() => {
    try { setWelcomeSeen(welcomeKey ? localStorage.getItem(welcomeKey) === "1" : true); } catch { setWelcomeSeen(true); }
  }, [welcomeKey]);
  const activeBizForWelcome = authedUser?.businesses?.find((b) => String(b.business_id) === String(getActiveBusinessId())) ?? authedUser?.businesses?.[0];
  const showWelcome = authed && role === "admin" && !welcomeSeen && !tourActive &&
    (tourPref === null || Boolean(activeBizForWelcome?.business_trial_active)) &&
    typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
  const closeWelcome = useCallback(() => {
    setWelcomeSeen(true);
    if (welcomeKey) { try { localStorage.setItem(welcomeKey, "1"); } catch { /* ignore */ } }
    // Bereichs-Touren nicht zusätzlich automatisch starten – der Kompass bleibt verfügbar.
    if (tourPref === null) setTourPreference("no");
  }, [welcomeKey, tourPref, setTourPreference]);


  // Beim ersten Öffnen eines Bereichs die passende Tour zeigen – nur wenn gewünscht.
  useEffect(() => {
    if (!authed || tourPref !== "yes") return;
    // Während eines Tutorials keine Bereichs-Tour dazwischenschieben.
    if (tourActiveRef.current) return;
    const id = window.setTimeout(() => { if (!tourActiveRef.current) startAreaTour(tab); }, 650);
    return () => window.clearTimeout(id);
  }, [authed, tab, tourPref, startAreaTour]);

  // Wiederholen über Hilfe: Tour des aktuellen Bereichs erneut anzeigen.
  useEffect(() => {
    const start = () => startAreaTour(tab, true);
    window.addEventListener("sonne:start-tour", start);
    return () => window.removeEventListener("sonne:start-tour", start);
  }, [startAreaTour, tab]);

  // Globale Bereichs-Navigation (z. B. „Im Profil ergänzen“ aus dem Platzhalter-Panel).
  useEffect(() => {
    const handler = (event: Event) => {
      const target = (event as CustomEvent).detail;
      if (typeof target === "string") setTab(target as TabId);
    };
    window.addEventListener("sonne:navigate", handler);
    return () => window.removeEventListener("sonne:navigate", handler);
  }, []);

  const allowedTabs = useMemo<TabId[]>(() => {
    const systemTabs: TabId[] = ["adminpanel", "fortbildungsbibliothek", "vorlagenbibliothek", "qmvorlagen", "qmhimport", "wissenswertes", "hilfe"];
    if (isSysAdmin) {
      // System-Admin im Mandanten: alle Mandanten-Tabs UND die System-Admin-Bereiche.
      if (!sysAdminInBusiness) return systemTabs;
      const allTabs = menuItems.map(m => m.id as TabId);
      return Array.from(new Set<TabId>([...allTabs, ...systemTabs, "profil", "hilfe"]));
    }
    if (role === "mitarbeitende") return ["dashboard", "qmhandbuch", "va", "fortbildungen", "prozesse", "wiki", "profil", "hilfe"];
    const base = menuItems
      .filter(m => m.id !== ("adminpanel" as TabKey))
      .filter(m => m.id !== ("fortbildungsbibliothek" as TabKey))
      .filter(m => m.id !== ("qmvorlagen" as TabKey)) // reines System-Admin-Feature
      .filter(m => m.id !== ("qmhimport" as TabKey)) // Einrichtungs-Service, nur System-Admin
      .map(m => m.id as TabId);
    return [...base, "hilfe"];
  }, [role, isSysAdmin, sysAdminInBusiness]);
  const visibleMenu = useMemo(
    () =>
      menuItems
        .filter(m => allowedTabs.includes(m.id as TabId))
        .filter(m => m.id !== ("iso9001" as TabKey) || (role === "admin" && iso9001Enabled))
        .filter(m => m.id !== ("iso15224" as TabKey) || (role === "admin" && iso15224Enabled)),
    [allowedTabs, role, iso9001Enabled, iso15224Enabled]
  );
  const headerTitle = useMemo(() => {
    if (role === "system_admin") return "System Admin";
    return orgLogoAlt || "Sonne QM";
  }, [role, orgLogoAlt]);
  const handleLogin = (user: AuthUser) => {
    setAuthedUser(user);
    // System-Admin startet immer in der System-Verwaltung (nicht in einem evtl.
    // noch gespeicherten Mandanten) — er kann danach gezielt einen Mandanten betreten.
    if (user.role === "system_admin") localStorage.removeItem(BIZ_SELECT_KEY);
    const mappedRole = resolveRoleFromUser(user) || "admin";
    setRole(mappedRole);
    // Auto-select first business if present and none selected yet
    if (!localStorage.getItem(BIZ_SELECT_KEY) && Array.isArray(user?.businesses) && user.businesses.length > 0) {
      const firstBizId = user.businesses[0]?.business_id;
      if (firstBizId) localStorage.setItem(BIZ_SELECT_KEY, String(firstBizId));
    } else if (user?.business_id && !localStorage.getItem(BIZ_SELECT_KEY)) {
      localStorage.setItem(BIZ_SELECT_KEY, String(user.business_id));
    }
    if (mappedRole === "system_admin") setTab("adminpanel");
    if (mappedRole === "mitarbeitende") setTab("dashboard");
  };

  const handleLogout = () => {
    logout();
  };

  const handleSystemAdminHome = () => {
    // Mandant verlassen: Auswahl loeschen und zurueck in die System-Verwaltung.
    // Der echte System-Admin-Nutzer bleibt eingeloggt (kein Pseudo-Nutzer mehr).
    localStorage.removeItem(BIZ_SELECT_KEY);
    localStorage.setItem(ROLE_KEY, "system_admin");
    window.location.reload();
  };

  useEffect(() => {
    if (role === "system_admin" && !allowedTabs.includes(tab)) {
      setTab("adminpanel");
      return;
    }
    if (role === "mitarbeitende" && !allowedTabs.includes(tab)) {
      setTab("va");
      return;
    }
    if (tab === "iso9001" && !(role === "admin" && iso9001Enabled)) {
      setTab("dashboard");
    }
    if (tab === "iso15224" && !(role === "admin" && iso15224Enabled)) {
      setTab("dashboard");
    }
    if (tab === "adminpanel" && !isSysAdmin) {
      setTab("dashboard");
    }
  }, [role, tab, allowedTabs, iso9001Enabled, iso15224Enabled, isSysAdmin]);

  useEffect(() => {
    if (!authed) {
      setOrgLogoUrl(null);
      setOrgLogoAlt(null);
      setOrgTrainingBgUrl(null);
      setOrgTrainingBgName(null);
      setIso9001Enabled(false);
      setIso15224Enabled(false);
      return;
    }
    const bizId = getActiveBusinessId();
    if (!bizId) {
      setOrgLogoUrl(null);
      setOrgLogoAlt(null);
      setOrgTrainingBgUrl(null);
      setOrgTrainingBgName(null);
      setIso9001Enabled(false);
      setIso15224Enabled(false);
      return;
    }
    let cancelled = false;
    const loadLogo = async () => {
      try {
        const resp = await authFetch(apiUrl(`/api/v1/businesses/${bizId}`));
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        setOrgLogoUrl(data.logo_url || null);
        setOrgLogoAlt(data.name || "Organisationslogo");
        setOrgTrainingBgUrl(data.training_background_url || null);
        setOrgTrainingBgName(data.training_background_name || null);
        setIso9001Enabled(!!data.iso_9001_enabled);
        setIso15224Enabled(!!data.iso_15224_enabled);
      } catch {
        if (!cancelled) {
          setOrgLogoUrl(null);
          setOrgLogoAlt(null);
          setOrgTrainingBgUrl(null);
          setOrgTrainingBgName(null);
          setIso9001Enabled(false);
          setIso15224Enabled(false);
        }
      }
    };
    const onBusinessUpdate = () => loadLogo();
    window.addEventListener("business:updated", onBusinessUpdate);
    loadLogo();
    return () => {
      cancelled = true;
      window.removeEventListener("business:updated", onBusinessUpdate);
    };
  }, [authed, authedUser]);

  if (verifyToken) {
    return <PublicVerify token={verifyToken} />;
  }

  if (resetToken) {
    return <ResetPassword token={resetToken} />;
  }

  return (
    <AppShell
      title={headerTitle}
      role={role}
      setRole={setRole}
      canSwitchRole={currentAuthUser()?.role === "system_admin"}
      onSystemAdminHome={(currentAuthUser()?.role === "system_admin" || !!currentAuthUser()?.impersonated_by_name) ? handleSystemAdminHome : undefined}
      onLogout={handleLogout}
      onOpenSearch={() => setSearchOpen(true)}
      authed={authed}
      orgLogoUrl={orgLogoUrl}
      orgLogoAlt={orgLogoAlt || undefined}
      showSidebar={authed}
      trialBanner={(() => {
        const activeBizId = getActiveBusinessId();
        const biz = authedUser?.businesses?.find((b) => String(b.business_id) === String(activeBizId)) ?? authedUser?.businesses?.[0];
        return biz?.business_trial_active ? <TrialBanner daysLeft={biz.business_trial_days_left} /> : null;
      })()}
      headerExtras={
        authed ? (
          <div className="flex items-center gap-2.5">
            {role !== "system_admin" && (
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-[9px] border border-[var(--line)] bg-white text-[var(--ink-mid)] transition hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)]"
                title="Einführungstour starten"
                aria-label="Einführungstour starten"
                onClick={() => startAreaTour(tab, true)}
              >
                <Compass className="h-[19px] w-[19px]" />
              </button>
            )}
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-[9px] border border-[var(--line)] bg-white text-[var(--ink-mid)] transition hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)]"
              title="Hilfe"
              aria-label="Hilfe öffnen"
              onClick={() => {
                setHelpReturnTab(tab);
                setTab("hilfe");
              }}
            >
              <HelpCircle className="h-[19px] w-[19px]" />
            </button>
          </div>
        ) : null
      }
      sidebar={<NavSidebar items={visibleMenu} activeItem={tab as unknown as TabKey} onItemClick={(id)=>setTab(id as unknown as TabId)} />}
    >
      <div className="pb-2" />
      {authed && (
        <GlobalSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onNavigate={(targetTab) => setTab(targetTab as TabId)}
          allowedTabs={allowedTabs as unknown as string[]}
        />
      )}
      {authed && tourActive && activeSteps.length > 0 && (
        <GuidedTour
          key={activeTutorialId ?? `area-${activeTourTab ?? ""}`}
          steps={activeSteps}
          onFinish={finishTour}
          onNavigate={(targetTab) => setTab(targetTab as TabId)}
        />
      )}
      {showWelcome && (
        <WillkommenDialog
          onLater={() => {
            closeWelcome();
            showGlobalToast("Die Anleitungen findest du jederzeit oben rechts unter „?“ (Hilfe) und im Dashboard unter „Erste Schritte“.", "info");
          }}
          onOrientation={() => {
            closeWelcome();
            setActiveTutorialId(null);
            setActiveTourTab(null);
            setActiveSteps(ORIENTIERUNG);
            setTourActive(true);
          }}
          onStartTutorial={() => {
            closeWelcome();
            startTutorial(TUTORIALS[0].id);
          }}
        />
      )}
      {authed && tourPref === null && role !== "system_admin" && !showWelcome && !(role === "admin" && !welcomeSeen) &&
        typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches && (
        <TourWelcomePrompt
          onAccept={() => { setTourPreference("yes"); startAreaTour(tab, true); }}
          onDecline={() => {
            setTourPreference("no");
            showGlobalToast("Kein Problem – du kannst die Tour jederzeit oben über „Tour“ starten.", "info");
          }}
        />
      )}
      {!authed ? (
        <LoginScreen onLogin={handleLogin} />
      ) : (
        <>
          {tab === "dashboard" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <DashboardModule role={role} authed={authed} onOpenTab={setTab} onOpenSearch={() => setSearchOpen(true)} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "adminpanel" && isSysAdmin && <ErrorBoundary><SystemAdminPanel /></ErrorBoundary>}
          {tab === "fortbildungsbibliothek" && isSysAdmin && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <FortbildungsbibliothekModule />
            </Suspense></ErrorBoundary>
          )}
          {tab === "vorlagenbibliothek" && (isSysAdmin || role === "admin") && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <TemplateLibraryModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "qmvorlagen" && isSysAdmin && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <QmVorlagenModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "qmhimport" && isSysAdmin && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <QmhImportModule />
            </Suspense></ErrorBoundary>
          )}
          {tab === "organisation" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <OrganisationModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "dokumente" && role === "admin" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <DokumenteModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "prozesse" && (role === "admin" || role === "mitarbeitende") && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <ProzesseModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "iso9001" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <Iso9001Module role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "iso15224" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <Iso15224Module role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "va" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <Verfahrensanweisungen role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "md" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <MDChecklistModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "pflegevisite" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <Pflegevisite role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "qmhandbuch" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <QmHandbuchModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "wiki" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <WikiModule />
            </Suspense></ErrorBoundary>
          )}
          {tab === "beschwerdemanagement" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <Complaints role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "wissenswertes" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <WissenswertesModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "profil" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <ProfileTabModule role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "fortbildungen" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <FortbildungenModule
                role={role}
                trainingBgUrl={orgTrainingBgUrl}
                trainingBgName={orgTrainingBgName}
              />
            </Suspense></ErrorBoundary>
          )}
          {tab === "mitarbeitende" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <EmployeesTabComponent role={role} />
            </Suspense></ErrorBoundary>
          )}
          {tab === "hilfe" && (
            <ErrorBoundary><Suspense fallback={<PageSkeleton />}>
              <HelpModule role={role} onClose={() => setTab(helpReturnTab || "dashboard")} />
            </Suspense></ErrorBoundary>
          )}
        </>
      )}
      <div className="pb-2" />

      {/* Session expired notification */}
      {sessionExpiredMsg && (
        <div className="fixed bottom-4 right-4 z-40 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg max-w-sm">
          <div className="font-semibold">Sitzung abgelaufen</div>
          <div className="text-sm mt-1">{sessionExpiredMsg}</div>
        </div>
      )}

      {/* App-weite Benachrichtigung, wenn ein Hintergrund-Export fertig ist */}
      {authed && <QmExportWatcher />}
    </AppShell>
  );
}

/**
 * Root app export
 */
export default function RootApp() {
  return (
    <Sentry.ErrorBoundary fallback={<div className="min-h-screen flex items-center justify-center text-sm text-slate-600">Ein unerwarteter Fehler ist aufgetreten. Bitte die Seite neu laden.</div>}>
      <App />
      <GlobalNotifications />
    </Sentry.ErrorBoundary>
  );
}
